import { useContext } from 'react'

import { faWarning } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Button, Form,
  ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgency from '@hooks/useAgency'

import EntitySelector from '@components/EntitySelector'

import { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { AgencyModel } from '@models/types'

import EntityListItem from './components/EntityListItem'
import useCopyAgencyDataModal from './hooks/useCopyAgencyDataModal'

const MODAL_KEY = 'CopyAgencyDataModal'

type ModalCallbacks = {
  closeModal: () => void,
  copyAgencyData: (agencyPayload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CopyAgencyDataModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, copyAgencyData } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agency } = modalPayload

  const copyAgencyDataPayload = useCopyAgencyDataModal()
  const {
    callbacks: { setState, toggleEntity },
    providerDependantEntities,
    selectableEntities,
    selectedEntityKeys,
    sourceAgencyId,
  } = copyAgencyDataPayload

  const agencyPayload = useAgency(agency)
  const { callbacks: { copyAgency }, updating } = agencyPayload

  const copyDataPayload = {
    callbacks: {
      action: copyAgency,
      afterAction: closeModal,
    },
    entityParams: {
      sourceAgencyId,
      destinationAgencyId: agency.id,
      selectedEntityKeys,
    },
    toastText: 'Copy Task Queued',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Copy" titleSecondLine="Agency Data" />

      <SidebarModal.Content>
        <Form.Field label="Copy Data to" boxProps={{ fontSize: 'small' }}>
          {agency.title}
        </Form.Field>

        <Form.Field direction="column" marginTop="large">
          <EntitySelector
            callbacks={{
              // @ts-expect-error TS2322
              selectItem: (agencySelected: AgencyModel) => setState({ sourceAgencyId: agencySelected?.id }),
            }}
            entityKey="agencies"
            label="* Copy Data From"
            selectedIds={[agency.id]}
            searchRequestOptions={{ brand_id: agency.brand_id }}
            // @ts-expect-error TS2322
            selectedItemId={sourceAgencyId}
          />
        </Form.Field>

        {!!sourceAgencyId && (
          <>
            <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
              <SidebarNoticeBox.Title>
                <FontAwesomeIcon icon={faWarning} />
                &nbsp;Override Data
              </SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                Copying data from another agency will override any existing data from this agency.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>

            <Form.Field label="Select Entities">
              {Object.values(selectableEntities).map(entity => (
                <EntityListItem
                  callbacks={{ toggleEntity: () => toggleEntity({ selectedEntity: entity.key }) }}
                  disabled={(
                    providerDependantEntities.includes(entity.key) && !selectedEntityKeys.includes('agency_providers')
                )}
                  entityName={entity.label}
                  isChecked={selectedEntityKeys.includes(entity.key)}
                  key={entity.key}
                />
              ))}
            </Form.Field>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!sourceAgencyId || !selectedEntityKeys.length}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => copyAgencyData(copyDataPayload)}
          size="large"
        >
          Copy Data
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CopyAgencyDataModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
